import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { VideoPost, SearchParams, VideoSearchResult } from '../models/e621.models';
import { E621Service } from './e621.service';

@Injectable({
  providedIn: 'root',
})
export class SlideshowService {
  private e621Service = inject(E621Service);

  private videos: VideoPost[] = [];
  private currentIndex = 0;
  private isPlaying = false;
  private userPaused = false;

  // Pagination state
  private currentSearchParams: SearchParams | null = null;
  private currentPage = 1;
  private isLoadingMoreVideos = false;
  // Shared promise for an in-flight page load, so concurrent callers await the
  // same request instead of skipping it.
  private loadMorePromise: Promise<void> | null = null;
  // Bumped whenever the queue is replaced, so async work started against an
  // older queue can detect that it is stale.
  private queueGeneration = 0;
  private hasMoreVideos = true;
  private currentVideo$ = new BehaviorSubject<VideoPost | null>(null);
  private isLoading$ = new BehaviorSubject<boolean>(false);
  private currentPosition$ = new BehaviorSubject<{
    current: number;
    total: number;
  }>({ current: 0, total: 0 });
  private errors$ = new Subject<string>();
  private queueComplete$ = new Subject<void>();
  private positionChanged$ = new Subject<{ query: string; position: number; videoId?: number }>();

  /**
   * Load new videos into the slideshow with search parameters
   */
  loadVideosWithSearch(videos: VideoPost[], searchParams: SearchParams, apiResult?: { totalFound: number; authRequiredCount: number; blacklistedCount: number }): void {
    console.log(
      `Loading ${videos.length} videos into slideshow with search params`
    );

    // Always reset state when loading new videos
    this.userPaused = false;
    this.stop();

    // Clear any pending loading timeout
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    this.isLoading$.next(false);

    this.queueGeneration++;
    this.videos = [...videos];
    this.currentIndex = 0;
    this.currentSearchParams = { ...searchParams };
    this.currentPage = searchParams.page || 1;

    // Determine if there are more videos available
    // If we got the full requested limit from the API (even if some were filtered),
    // there are likely more videos available
    const requestedLimit = searchParams.limit || 200;
    if (apiResult) {
      // Use API result info to make a better determination
      this.hasMoreVideos = apiResult.totalFound >= requestedLimit;
      console.log(`API returned ${apiResult.totalFound} posts, ${apiResult.authRequiredCount} auth-required, ${apiResult.blacklistedCount} blacklisted, ${videos.length} usable videos`);
    } else {
      // Fallback to old logic
      this.hasMoreVideos = videos.length === requestedLimit;
    }

    this.updateCurrentVideo();
    this.updatePosition();

    // Add a defensive timeout to ensure loading state is cleared even if video events don't fire
    setTimeout(() => {
      if (this.isLoading$.getValue()) {
        console.log('[VIDEO LOADING] Defensive clear: ensuring loading state is false after video setup');
        this.isLoading$.next(false);
      }
    }, 2000);
  }

  /**
   * Load new videos into the slideshow (legacy method)
   */
  loadVideos(videos: VideoPost[]): void {
    console.log(`Loading ${videos.length} videos into slideshow`);

    // Always reset state when loading new videos, even if they're the same
    this.userPaused = false;
    this.stop(); // Stop current playback

    // Clear any pending loading timeout
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    this.isLoading$.next(false); // Reset loading state

    this.queueGeneration++;
    this.videos = [...videos];
    this.currentIndex = 0;
    this.currentSearchParams = null; // No search params, so no pagination
    this.hasMoreVideos = false;

    this.updateCurrentVideo();
    this.updatePosition();

    // Add a defensive timeout to ensure loading state is cleared even if video events don't fire
    setTimeout(() => {
      if (this.isLoading$.getValue()) {
        console.log('[VIDEO LOADING] Defensive clear: ensuring loading state is false after video setup');
        this.isLoading$.next(false);
      }
    }, 2000);
  }

  /**
   * Get the current video observable
   */
  getCurrentVideo() {
    return this.currentVideo$.asObservable();
  }

  /**
   * Get the current video synchronously
   */
  getCurrentVideoSync(): VideoPost | null {
    return this.videos[this.currentIndex] || null;
  }

  /**
   * Get the loading state observable
   */
  getLoadingState() {
    return this.isLoading$.asObservable();
  }

  /**
   * Get the current position observable
   */
  getCurrentPosition() {
    return this.currentPosition$.asObservable();
  }
  /**
   * Get error messages observable
   */
  getErrors() {
    return this.errors$.asObservable();
  }

  /**
   * Get queue-complete observable.
   * Emits when the last video in the queue has been reached and there are no
   * more videos to load from the API.
   */
  getQueueComplete() {
    return this.queueComplete$.asObservable();
  }

  /**
   * Get position changes observable
   */
  getPositionChanges() {
    return this.positionChanged$.asObservable();
  }

  /**
   * Get pagination status
   */
  getPaginationStatus() {
    return {
      isLoadingMore: this.isLoadingMoreVideos,
      hasMoreVideos: this.hasMoreVideos,
      currentPage: this.currentPage,
      totalVideos: this.videos.length,
    };
  }

  /**
   * Start the slideshow
   */
  start(): void {
    if (this.videos.length === 0) {
      this.errors$.next('No videos loaded');
      return;
    }

    this.isPlaying = true;
    this.userPaused = false;
    console.log('Slideshow started');
  }

  /**
   * Stop the slideshow (internal use / system reset)
   */
  stop(): void {
    this.isPlaying = false;
    console.log('Slideshow stopped');
  }

  /**
   * Pause the slideshow due to explicit user action.
   * Unlike stop(), this sets the userPaused flag so that navigation
   * does not resume playback automatically.
   */
  pause(): void {
    this.isPlaying = false;
    this.userPaused = true;
    console.log('Slideshow paused by user');
  }

  /**
   * Check if slideshow is playing
   */
  getIsPlaying(): boolean {
    return this.isPlaying;
  }

  /**
   * Check if the user explicitly paused the slideshow.
   * Returns true when the user pressed pause and has not yet started playing again
   * or loaded a new set of videos.
   */
  isUserPaused(): boolean {
    return this.userPaused;
  }

  /**
   * Go to next video
   */
  next(): void {
    if (this.videos.length === 0) return;

    const remainingVideos = this.videos.length - this.currentIndex - 1;
    const isAtEnd = remainingVideos <= 0;

    if (isAtEnd) {
      // At the tail of the loaded queue. Never wrap back to the first video
      // while the API still has pages for us — that silently restarts the
      // slideshow instead of continuing into the next page.
      if (this.hasMoreVideos && this.currentSearchParams) {
        console.log('Reached end of loaded videos, loading next page before advancing');
        this.loadNextPageThenAdvance();
        return;
      }

      console.log('Reached end of all available videos');
      this.errors$.next('No more videos available');
      this.queueComplete$.next();
      return;
    }

    // Prefetch ahead of the tail so navigation stays smooth. Be aggressive
    // here since blacklisting can thin a page out considerably.
    if (remainingVideos <= 5 && this.hasMoreVideos && this.currentSearchParams) {
      console.log(`Triggering pagination: ${remainingVideos} videos remaining`);
      this.loadMoreVideos();
    }

    this.currentIndex = this.currentIndex + 1;
    console.log(
      `Moving to video ${this.currentIndex + 1} of ${this.videos.length}`
    );
    this.updateCurrentVideo();
    this.updatePosition();
  }

  /**
   * Load the next page and then step into it.
   *
   * Used when the user reaches the end of the loaded queue but the API still
   * has results. Shares any prefetch already in flight rather than racing it,
   * and emits queue-complete only once the API genuinely has nothing left.
   */
  private async loadNextPageThenAdvance(): Promise<void> {
    const generationBefore = this.queueGeneration;
    const indexBefore = this.currentIndex;

    await this.loadMoreVideos();

    // A new search (or a clear) landed while we were loading — that queue owns
    // the position now, so leave it alone.
    if (this.queueGeneration !== generationBefore) return;

    // The user navigated somewhere else while we were loading.
    if (this.currentIndex !== indexBefore) return;

    if (this.currentIndex < this.videos.length - 1) {
      this.currentIndex = this.currentIndex + 1;
      console.log(
        `Moving to video ${this.currentIndex + 1} of ${this.videos.length}`
      );
      this.updateCurrentVideo();
      this.updatePosition();
      return;
    }

    console.log('Reached end of all available videos');
    this.errors$.next('No more videos available');
    this.queueComplete$.next();
  }

  /**
   * Go to previous video
   */
  previous(): void {
    if (this.videos.length === 0) return;

    // Don't wrap around if we're already at the first video
    if (this.currentIndex === 0) {
      console.log('Already at first video, not moving');
      return;
    }

    this.currentIndex = this.currentIndex - 1;
    console.log(
      `Moving to video ${this.currentIndex + 1} of ${this.videos.length}`
    );
    this.updateCurrentVideo();
    this.updatePosition();
  }
  /**
   * Clear all videos
   */
  clear(): void {
    this.queueGeneration++;
    this.videos = [];
    this.currentIndex = 0;
    this.isPlaying = false;
    this.userPaused = false;
    this.currentSearchParams = null;
    this.currentPage = 1;
    this.hasMoreVideos = true;
    this.isLoadingMoreVideos = false;
    this.currentVideo$.next(null);
    this.updatePosition();
    console.log('Slideshow cleared');
  }
  /**
   * Restore to a specific position (simplified version)
   */
  async restoreToPosition(targetPosition: number): Promise<void> {
    console.log(`Attempting to restore to position ${targetPosition}`);

    // Simple implementation: just go to the position if it exists
    const targetIndex = targetPosition - 1; // Convert to 0-based index
    if (targetIndex >= 0 && targetIndex < this.videos.length) {
      this.currentIndex = targetIndex;
      console.log(
        `Restored to video ${this.currentIndex + 1} of ${this.videos.length}`
      );
      this.updateCurrentVideo();
      this.updatePosition();
    } else {
      console.warn(
        `Could not restore to position ${targetPosition}, current videos: ${this.videos.length}`
      );
    }
  }

  /**
   * Restore to a specific video by ID (more reliable than position)
   */
  async restoreToVideoId(targetVideoId: number, searchParams: SearchParams): Promise<boolean> {
    console.log(`Attempting to restore to video ID ${targetVideoId}`);

    // First, check if the video is in the current results
    const targetIndex = this.videos.findIndex(video => video.id === targetVideoId);
    if (targetIndex >= 0) {
      this.currentIndex = targetIndex;
      console.log(`Found video ${targetVideoId} at position ${this.currentIndex + 1}`);
      this.updateCurrentVideo();
      this.updatePosition();
      return true;
    }

    // Video not found in current page, search nearby pages
    console.log(`Video ${targetVideoId} not found in current page, searching nearby pages...`);

    const currentPage = searchParams.page || 1;

    // Search previous and next few pages
    for (let pageOffset = -3; pageOffset <= 5; pageOffset++) {
      if (pageOffset === 0) continue; // Skip current page (already checked)

      const searchPage = Math.max(1, currentPage + pageOffset);
      console.log(`Searching page ${searchPage} for video ${targetVideoId}...`);

      try {
        const result: VideoSearchResult = await firstValueFrom(this.e621Service.searchVideos({
          ...searchParams,
          page: searchPage
        }));

        if (result) {
          const foundIndex = result.videos.findIndex((video: VideoPost) => video.id === targetVideoId);
          if (foundIndex >= 0) {
            console.log(`Found video ${targetVideoId} on page ${searchPage}, loading that page...`);

            // Load the videos from the page where we found the target
            this.loadVideosWithSearch(result.videos, { ...searchParams, page: searchPage }, {
              totalFound: result.totalFound,
              authRequiredCount: result.authRequiredCount,
              blacklistedCount: result.blacklistedCount
            });

            // Set current index to the found video
            this.currentIndex = foundIndex;
            this.updateCurrentVideo();
            this.updatePosition();
            return true;
          }
        }
      } catch (error) {
        console.warn(`Failed to search page ${searchPage}:`, error);
      }
    }

    console.warn(`Video ${targetVideoId} not found in nearby pages, starting from beginning`);
    return false;
  }

  /**
   * Update the current video and notify observers
   */
  private updateCurrentVideo(): void {
    const currentVideo = this.videos[this.currentIndex] || null;
    this.currentVideo$.next(currentVideo);
    console.log(
      `Current video updated: ${this.currentIndex + 1} of ${this.videos.length}`
    );
  }

  /**
   * Update position and notify observers
   */
  private updatePosition(): void {
    const position = {
      current: this.currentIndex + 1,
      total: this.videos.length,
    };
    this.currentPosition$.next(position);

    // Emit position change for history tracking
    if (this.currentSearchParams && this.videos[this.currentIndex]) {
      this.positionChanged$.next({
        query: this.currentSearchParams.tags || '',
        position: this.currentIndex + 1,
        videoId: this.videos[this.currentIndex].id,
      });
    }
  }

  /**
   * Load more videos from the next page.
   *
   * Callers that need to act on the result (rather than just prefetching) can
   * await this: a load already in flight is shared rather than skipped, so
   * awaiting always resolves once the page has actually landed.
   */
  private loadMoreVideos(): Promise<void> {
    if (!this.currentSearchParams || !this.hasMoreVideos) {
      return Promise.resolve();
    }

    if (this.loadMorePromise) {
      return this.loadMorePromise;
    }

    this.loadMorePromise = this.performLoadMoreVideos().finally(() => {
      this.loadMorePromise = null;
    });

    return this.loadMorePromise;
  }

  /**
   * Fetch and append the next page. Use loadMoreVideos() instead — it
   * de-duplicates concurrent calls.
   */
  private async performLoadMoreVideos(): Promise<void> {
    if (!this.currentSearchParams || !this.hasMoreVideos) {
      return;
    }

    this.isLoadingMoreVideos = true;
    const generationAtStart = this.queueGeneration;
    const nextPage = this.currentPage + 1;
    const searchParams = { ...this.currentSearchParams, page: nextPage };

    try {
      const result = await this.e621Service
        .searchVideos(searchParams)
        .toPromise();

      // A new search replaced the queue while this page was in flight —
      // appending it now would splice results from the old search into the new
      // queue.
      if (this.queueGeneration !== generationAtStart) {
        console.log('Discarding stale page: the queue was replaced while loading');
        return;
      }

      if (result && result.videos.length > 0) {
        this.videos.push(...result.videos);
        this.currentPage = nextPage;

        // Use the API result to determine if there are more videos
        // If the API returned the full requested limit, there are likely more
        const requestedLimit = searchParams.limit || 200;
        this.hasMoreVideos = result.totalFound >= requestedLimit;

        console.log(
          `Loaded ${result.videos.length} more videos from page ${nextPage} (API returned ${result.totalFound} posts, ${result.authRequiredCount} auth-required, ${result.blacklistedCount || 0} blacklisted)`
        );
        if (result.authRequiredCount > 0) {
          console.log(
            `${result.authRequiredCount} additional videos on page ${nextPage} require authentication`
          );
        }
        this.updatePosition();
      } else {
        this.hasMoreVideos = false;
        console.log('No more videos available from API');
      }
    } catch (error) {
      console.error('Failed to load more videos:', error);
      this.errors$.next('Failed to load more videos');
    } finally {
      this.isLoadingMoreVideos = false;
    }
  }

  private loadingTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Handle video load start
   */
  onVideoLoadStart(): void {
    // Clear any existing timeout
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }

    this.isLoading$.next(true);

    // Set a defensive timeout to clear loading state if video doesn't load within 10 seconds
    this.loadingTimeout = setTimeout(() => {
      console.warn('[VIDEO LOADING] Timeout reached - forcing loading state to false');
      this.isLoading$.next(false);
      this.loadingTimeout = null;
    }, 10000);
  }

  /**
   * Handle video load complete
   */
  onVideoLoadComplete(): void {
    // Clear the timeout since video loaded successfully
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
    this.isLoading$.next(false);
  }

  /**
   * Handle video error
   */
  onVideoError(message: string): void {
    // Clear the timeout since we got an error
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
    this.isLoading$.next(false);
    this.errors$.next(message);
  }

  /**
   * Handle video ended
   */
  onVideoEnded(): void {
    // Auto-advance to next video when current one ends
    this.next();
  }

  /**
   * Go to the first video
   */
  goToFirst(): void {
    if (this.videos.length === 0) return;

    this.currentIndex = 0;
    console.log(`Resetting to first video (1 of ${this.videos.length})`);
    this.updateCurrentVideo();
    this.updatePosition();
  }

  /**
   * Go back by specified number of videos
   */
  goBack(count: number): void {
    if (this.videos.length === 0) return;

    const newIndex = Math.max(0, this.currentIndex - count);
    this.currentIndex = newIndex;
    console.log(
      `Moving back ${count} videos to position ${this.currentIndex + 1} of ${
        this.videos.length
      }`
    );
    this.updateCurrentVideo();
    this.updatePosition();
  }

  /**
   * Go forward by specified number of videos
   */
  async goForward(count: number): Promise<void> {
    if (this.videos.length === 0) return;

    const targetIndex = this.currentIndex + count;

    // Check if we need to load more videos to reach the target
    if (
      targetIndex >= this.videos.length &&
      this.hasMoreVideos &&
      this.currentSearchParams
    ) {
      console.log(
        `Need to load more videos to reach position ${targetIndex + 1}`
      );

      // Calculate how many more pages we might need
      const videosNeeded = targetIndex - this.videos.length + 1;
      const limit = this.currentSearchParams.limit || 200;
      const pagesNeeded = Math.ceil(videosNeeded / limit);

      // Load multiple pages if needed
      for (let i = 0; i < pagesNeeded && this.hasMoreVideos; i++) {
        await this.loadMoreVideos();
      }
    }

    // Set the new index, but don't go beyond what we have
    const newIndex = Math.min(targetIndex, this.videos.length - 1);

    if (newIndex >= this.videos.length) {
      console.log('Cannot go forward: no more videos available');
      this.errors$.next('No more videos available');
      return;
    }

    this.currentIndex = newIndex;
    console.log(
      `Moving forward ${count} videos to position ${this.currentIndex + 1} of ${
        this.videos.length
      }`
    );
    this.updateCurrentVideo();
    this.updatePosition();
  }

  /**
   * Get current videos array
   */
  getVideos(): VideoPost[] {
    return [...this.videos];
  }

  /**
   * Get current video index
   */
  getCurrentIndex(): number {
    return this.currentIndex;
  }

  /**
   * Get current page number
   */
  getCurrentPage(): number {
    return this.currentPage;
  }

  /**
   * Get total number of videos loaded
   */
  getTotalVideosLoaded(): number {
    return this.videos.length;
  }

  /**
   * Get current search parameters
   */
  getCurrentSearchParams(): SearchParams | null {
    return this.currentSearchParams ? { ...this.currentSearchParams } : null;
  }
}
